import {withRedirects} from '@/lib/withRedirects';
import galleryActions from '@/redux/actions/galleryActions';
import {loadPlpSeoText, setSeoText} from '@/redux/actions/plpSeoTextActions';
import {
  filterUsedByUser,
  pageView,
  paginationUsedByUser,
  sortUsedByUser,
  urlChanged,
} from '@/redux/actions/trackingActions';
import {selectGalleryData} from '@/redux/reducers/galleryReducer';
import {selectPlpSeoTextData} from '@/redux/reducers/plpSeoTextReducer';
import cacheStore from '@/services/cache-service';
import {prefix, validateParams} from '@/services/plp-url-params';
import Bugsnag from '@bugsnag/js';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {queryEditorsPick} from '@/database/editors-pick';

import configs from '@/utils/configs';
import constantsFactory from '@/utils/constants';
import {fetchJson} from '@/utils/fetch';
import paramsHelper from '@/utils/paramsHelper';

import CollapsibleText from '@/molecules/CollapsibleText/CollapsibleText';
import Gallery from '@/organisms/Gallery/Gallery';
import PageLayout from '@/organisms/Layout/Page/PageLayout';
import SeoMetadataService from '@/services/seo-metadata-service';
import {getAdCards} from '@/ssr/product/getAdCards';
import canonicalStringForOnSale from '@/utils/art/canonicalStringForOnSale';
import {isIndexable} from '@/utils/art/isIndexable';
import cookie from '@/utils/helpers/cookie';
import {decodeSearchText} from '@/utils/helpers/string';
import {StyledPlpPage, StyledSubTitle, StyledTitle} from './styles';
const PRODUCTS_PER_PAGE = 60;

const PlpPage = ({
  editorsPicks,
  initPage,
  adCards,
  params,
  initialProductSearchResponse,
  seoData,
}) => {
  const activePage = useRef(initPage);
  const prevPage = useRef(activePage.current - 1);
  const nextPage = useRef(activePage.current + 1);
  const dispatch = useDispatch();
  const seoTextData = useSelector(selectPlpSeoTextData);
  const router = useRouter();
  const searchData = useSelector(selectGalleryData);
  const [canonicalBase, setCanonicalBase] = useState(
    prefix(paramsHelper(validateParams(params)).sort(configs.urlParams).result).join('/'),
  );

  useEffect(() => {
    router.beforePopState(({as}) => {
      if (as !== router.asPath) {
        // Will run when leaving the current page; on back/forward actions
        window.location.reload();
      }
      return true;
    });
  }, []);

  useEffect(() => {
    trackPageView();
  }, [canonicalBase]);

  const buildCanonicalBase = (urlParams) => {
    let page = urlParams.find((param) => param.includes('page-'));
    page = page ? parseInt(page.replace('page-', '')) : 1;
    const newCanonicalBase = urlParams.filter((param) => !param.includes('page-')).join('/');
    activePage.current = page;
    nextPage.current = page + 1;
    prevPage.current = page - 1;
    if (newCanonicalBase === canonicalBase) {
      trackPageView();
    } else {
      setCanonicalBase(newCanonicalBase);
    }
  };

  const trackPageView = () => {
    dispatch(pageView(canonicalString(activePage.current).split(`${process.env.HOST}`).pop()));
  };

  const canonicalString = (page) => {
    let cleanedCanonicalParamsString = '';
    if (canonicalBase) {
      const cleanedCanonicalBase = canonicalBase.replace(/sort-best_match\/?/, '');
      cleanedCanonicalParamsString = cleanedCanonicalBase ? cleanedCanonicalBase + '/' : '';
    }

    return `${process.env.HOST}/art/${cleanedCanonicalParamsString}${
      page > 1 ? `page-${page}/` : ''
    }`;
  };

  return (
    <StyledPlpPage>
      <Head>
        <title>
          {seoTextData.meta_page_title ?? seoData?.meta_page_title ?? 'Original art for sale'}
          {activePage.current > 1 ? ` | ${activePage.current} page` : ''}
        </title>
        <meta
          name="description"
          content={
            seoTextData.meta_page_description ??
            seoData?.meta_page_description ??
            `Art for the soul. Art for life. Art for your home. Artfinder connects passionate artists with art lovers around the world.`
          }
        />
        <meta
          name="robots"
          content={isIndexable(router.asPath) ? 'index,follow' : 'noindex,nofollow'}
        />
        <meta property="fb:app_id" content={process.env.SOCIAL_AUTH_FACEBOOK_KEY} />
        <meta property="og:site_name" content="Artfinder" />
        <meta
          property="og:description"
          content={
            seoTextData.meta_page_description ??
            seoData?.meta_page_description ??
            `Art for the soul. Art for life. Art for your home. Artfinder connects passionate artists with art lovers around the world.`
          }
        />
        <meta
          property="og:image"
          content="https://d2m7ibezl7l5lt.cloudfront.net/img/v2/default-share-image.bca336466cdd.jpg"
        />
        <link
          rel="canonical"
          href={canonicalStringForOnSale(canonicalString(activePage.current))}
        />
        {activePage.current > 1 && <link rel="prev" href={canonicalString(prevPage.current)} />}
        {activePage.current < Math.ceil(searchData.totalCount / PRODUCTS_PER_PAGE) && (
          <link rel="next" href={canonicalString(nextPage.current)} />
        )}
      </Head>

      <PageLayout editorsPicks={editorsPicks}>
        <main>
          <StyledTitle>{seoTextData.page_title ?? seoData?.page_title}</StyledTitle>

          <StyledSubTitle>
            <CollapsibleText elementKey={`description-${params.join('-')}`} fontSize={12}>
              {seoTextData.page_description ?? seoData?.page_description}
            </CollapsibleText>
          </StyledSubTitle>

          <Gallery
            itemsPerCall={12}
            itemsEagerLoadedPerCall={4}
            reduxActions={{
              seedSearch: galleryActions({galleryType: 'plp'}).seedSearch,
              SEO: {get: loadPlpSeoText, set: setSeoText},
              filters: {
                search: galleryActions({galleryType: 'plp'}).search,
                clearSearch: galleryActions({galleryType: 'plp'}).clearSearchResults,
                searchPreview: galleryActions({galleryType: 'plp'}).searchPreview,
                GA: {
                  filterUsed: filterUsedByUser,
                  sortingUsed: sortUsedByUser,
                },
              },
              GA: {paginationUsed: paginationUsedByUser, urlChanged: urlChanged},
            }}
            returnUrlParams={buildCanonicalBase}
            basePath={'/art/'}
            urlParams={`${canonicalBase}`}
            initData={{
              page: initPage,
              seo: seoData,
              searchResult: initialProductSearchResponse,
              adCards: adCards,
            }}
          />
        </main>
      </PageLayout>
    </StyledPlpPage>
  );
};

export default PlpPage;

const getEditorsPick = async () => {
  // Get editors pick from api or from db according to settings
  const {CACHE} = constantsFactory();

  // redis cache
  let editorsPickFromCache = null;
  try {
    editorsPickFromCache = await cacheStore.get('last_5_editors_pick');
  } catch (e) {
    console.log(`[Product] Error. editors pick cache get() - ${e}`);
  }

  // eslint-disable-next-line max-len
  const promises: any[] = [];
  if (!editorsPickFromCache) {
    const editorsPickCall = queryEditorsPick();
    promises.push(editorsPickCall.then((r) => ({name: 'editorsPicks', result: r})));
  }

  const result = await Promise.all(promises);
  const editorsPicks =
    editorsPickFromCache ?? result.filter((x) => x.name == 'editorsPicks')?.[0]?.result;

  // set on cache anything we want
  if (!editorsPickFromCache) {
    try {
      await cacheStore.set(
        'last_5_editors_pick',
        editorsPicks,
        CACHE.EDITORS_PICK_CACHE_TTL_MILLISECONDS,
      );
    } catch (e) {
      console.log(`[Product] Error. editors pick cache set() - ${e}`);
    }
  }

  return editorsPicks;
};

const getInitialProductSearch = async (initialParams) => {
  const decodedParams = initialParams.q
    ? {...initialParams, q: encodeURIComponent(decodeSearchText(initialParams.q))}
    : initialParams;

  const {API, TIMEOUT} = constantsFactory();
  const maxCallsPerPage = 5;
  const page = decodedParams.page ?? 1;
  const callNumber = (page - 1) * maxCallsPerPage + 1;
  const params = {...decodedParams, page: callNumber};
  const query = paramsHelper(params).objectToQueryString();
  const SEARCH_ENDPOINT = `${process.env.HOST}${API.PRODUCT_SEARCH_API}${query}`;

  return fetchJson(SEARCH_ENDPOINT, {timeout: TIMEOUT.PRODUCT_SEARCH_API_TIMEOUT})
    .then((res) => {
      return res;
    })
    .catch((err) => {
      console.log('WARNING. PLP initial product fetch: ', err);
      return null;
    });
};

const getSeoText = async (initialParams, currency) => {
  const decodedParams = initialParams.q
    ? {...initialParams, q: decodeSearchText(initialParams.q)}
    : initialParams;
  const seoMetadataService = SeoMetadataService.getInstance();
  const seoMetadata = await seoMetadataService.getSeoMetadata(decodedParams, currency);
  return seoMetadata;
};

// eslint-disable-next-line
export const getServerSideProps = withRedirects(async (context) => {
  const {CACHE} = constantsFactory();

  try {
    const getPage = () => {
      const defaultPage = 1;
      let page: number | null = null;

      const params = context.query.param;

      if (params && params instanceof Array) {
        params.every((param) => {
          const [key, value] = param.split(/-(.+)/).map((el) => el.replace(/\s+/g, ' ').trim());
          if (key === 'page' && Number.isInteger(Number(value)) && Number(value) > 0) {
            page = Number(value);
            return false;
          }
          return true;
        });
      }

      return page ? page : defaultPage;
    };

    const rawParams = context.query.param ? [...context.query.param] : [];
    const validatedParams = paramsHelper(validateParams(rawParams)).unwrapCategories().result;

    // Get currency from cookies
    const {req} = context;
    const currency = cookie.extractFromString('af-currency', req.headers.cookie) || 'GBP';
    const seoData = await getSeoText(validateParams(rawParams), currency);
    const editorsPicks = await getEditorsPick();
    const initialProductSearchResponse = await getInitialProductSearch(validatedParams);
    const initPage = getPage();
    const adCards = await getAdCards();
    const params =
      context.query.param && context.query.param instanceof Array
        ? context.query.param.filter((param) => !param.includes('page-'))
        : [];

    // Set cache headers if we have been able to get initial products
    if (initialProductSearchResponse) {
      context.res.setHeader(
        'Cache-Control',
        `public, s-maxage=${CACHE.PLP_CACHE_TTL_SECONDS}, stale-while-revalidate=${CACHE.PLP_CACHE_STALE_WHILE_REVALIDATE_TTL_SECONDS}`,
      );
    } else {
      console.log(`[PLP] Warning. Failed to get initial products: [${context.resolvedUrl}]`);
    }

    return {
      props: {
        editorsPicks,
        initPage,
        adCards,
        params,
        initialProductSearchResponse,
        seoData,
      },
    };
  } catch (e) {
    console.log(`[PLP] Error. Invalid Rendering - page: [${context.resolvedUrl}]`);
    console.log(e);
    Bugsnag.notify(
      new Error(`[PLP] Error. Invalid Rendering - page: [${context.resolvedUrl}]`),
      (event) => {
        event.severity = 'error';
        event.unhandled = true;
      },
    );

    // TODO: sending 404 not found because returning the error is causing infinite loop
    return {
      notFound: true,
    };
  }
});
